import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Hook {
  id: string;
  name: string;
  icon: string;
  description: string;
  trigger: string;
  example: string;
  active?: boolean;
}

interface BuildPhase {
  id: string;
  name: string;
  prompts: string[];
  currentPrompt: number;
  status: 'pending' | 'active' | 'completed' | 'error';
  hooks: string[];
}

interface AgentState {
  phase: string;
  step: number;
  testsWritten: number;
  testsPassing: number;
  filesCreated: string[];
  errors: string[];
  isRunning: boolean;
}

export default function ClaudeAgentBuilder() {
  const navigate = useNavigate();
  const [isRunning, setIsRunning] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [log, setLog] = useState<string[]>([]);
  const [agentState, setAgentState] = useState<AgentState>({
    phase: 'requirements',
    step: 0,
    testsWritten: 0,
    testsPassing: 0,
    filesCreated: [],
    errors: [],
    isRunning: false
  });

  const hooks: Hook[] = [
    {
      id: 'stop',
      name: 'Stop Hook',
      icon: '✋',
      description: 'Triggers when Claude finishes responding',
      trigger: 'After each response',
      example: 'Auto-prompt: "Continue to next step"'
    },
    {
      id: 'error',
      name: 'Error Hook',
      icon: '❌',
      description: 'Triggers on errors/failures',
      trigger: 'When operations fail',
      example: 'Auto-prompt: "Fix the error and retry"'
    },
    {
      id: 'success',
      name: 'Success Hook',
      icon: '✅',
      description: 'Triggers on successful operations',
      trigger: 'When operations succeed',
      example: 'Auto-prompt: "Move to next phase"'
    },
    {
      id: 'tool',
      name: 'Tool Hook',
      icon: '🔧',
      description: 'Triggers before/after tool use',
      trigger: 'Around tool execution',
      example: 'Track: File changes, API calls'
    },
    {
      id: 'context',
      name: 'Context Hook',
      icon: '📝',
      description: 'Monitors context/token usage',
      trigger: 'Near context limit',
      example: 'Auto-prompt: "Checkpoint and continue"'
    }
  ];

  const buildPhases: BuildPhase[] = [
    {
      id: 'requirements',
      name: '📋 Requirements → Tests',
      prompts: [
        'Generate comprehensive test suite for [FEATURE]',
        'Run all tests to see failures',
        'Create implementation plan based on failing tests'
      ],
      currentPrompt: 0,
      status: 'pending',
      hooks: ['stop', 'tool']
    },
    {
      id: 'implementation',
      name: '🔨 Tests → Implementation',
      prompts: [
        'Implement code to make test #1 pass',
        'Run test #1',
        'Fix any errors or move to test #2',
        'Repeat for all tests'
      ],
      currentPrompt: 0,
      status: 'pending',
      hooks: ['stop', 'error', 'success']
    },
    {
      id: 'refinement',
      name: '✨ Implementation → Refinement',
      prompts: [
        'Run all tests together',
        'Refactor any code over 100 lines',
        'Add error handling for edge cases',
        'Optimize performance bottlenecks'
      ],
      currentPrompt: 0,
      status: 'pending',
      hooks: ['stop', 'success', 'tool']
    },
    {
      id: 'deployment',
      name: '🚀 Refinement → Deployment',
      prompts: [
        'Build and check for errors',
        'Deploy to production',
        'Verify deployment',
        'Run smoke tests'
      ],
      currentPrompt: 0,
      status: 'pending',
      hooks: ['stop', 'success', 'error']
    }
  ];

  const patterns = [
    {
      name: 'TDD Loop',
      icon: '🔄',
      steps: [
        'Write test for feature',
        'Run test to see it fail',
        'Write minimal code to pass',
        'Run test to see it pass',
        'Refactor if needed',
        'Commit changes'
      ]
    },
    {
      name: 'Debug Loop',
      icon: '🐛',
      steps: [
        'Identify the error',
        'Locate root cause',
        'Implement fix',
        'Test the fix',
        'Verify no regressions',
        'Document solution'
      ]
    },
    {
      name: 'Feature Loop',
      icon: '⭐',
      steps: [
        'Define requirements',
        'Create test suite',
        'Build UI components',
        'Implement logic',
        'Connect to API',
        'Add error handling',
        'Test end-to-end',
        'Deploy'
      ]
    }
  ];

  const simulateStep = () => {
    const phases = [...buildPhases];
    const phase = phases[currentPhase];

    if (!phase) return;

    const newLog = [...log];
    const prompt = phase.prompts[currentStep];

    newLog.push(`[${new Date().toLocaleTimeString()}] 🤖 Executing: ${prompt}`);

    if (Math.random() > 0.1) {
      newLog.push(`[${new Date().toLocaleTimeString()}] ✅ Success: ${prompt}`);

      const newState = { ...agentState };
      if (prompt.includes('test')) {
        newState.testsWritten += Math.floor(Math.random() * 5) + 1;
      }
      if (prompt.includes('pass')) {
        newState.testsPassing += 1;
      }
      if (prompt.includes('Implement')) {
        newState.filesCreated.push(`Component${newState.filesCreated.length + 1}.tsx`);
      }

      setAgentState(newState);

      if (currentStep < phase.prompts.length - 1) {
        setCurrentStep(currentStep + 1);
      } else if (currentPhase < buildPhases.length - 1) {
        setCurrentPhase(currentPhase + 1);
        setCurrentStep(0);
        newLog.push(`[${new Date().toLocaleTimeString()}] 🎯 Phase Complete: ${phase.name}`);
      } else {
        setIsRunning(false);
        newLog.push(`[${new Date().toLocaleTimeString()}] 🎉 BUILD COMPLETE!`);
      }
    } else {
      newLog.push(`[${new Date().toLocaleTimeString()}] ❌ Error: ${prompt} failed`);
      newLog.push(`[${new Date().toLocaleTimeString()}] 🔄 Error Hook: Retrying...`);
    }

    setLog(newLog);
  };

  useEffect(() => {
    if (isRunning) {
      const timer = setTimeout(simulateStep, 2000);
      return () => clearTimeout(timer);
    }
  }, [isRunning, currentPhase, currentStep]);

  const startAutonomousBuild = () => {
    setIsRunning(true);
    setLog([`[${new Date().toLocaleTimeString()}] 🚀 Starting Autonomous Build...`]);
    setCurrentPhase(0);
    setCurrentStep(0);
    setAgentState({
      phase: 'requirements',
      step: 0,
      testsWritten: 0,
      testsPassing: 0,
      filesCreated: [],
      errors: [],
      isRunning: true
    });
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">🤖 Claude Autonomous Agent Builder</h1>
              <p className="text-gray-400">Watch Claude build complete features using hooks & prompt sequences</p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              ← Back to App
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="md:col-span-2">
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">🪝 Available Hooks</h2>
              <div className="grid grid-cols-2 gap-4">
                {hooks.map(hook => (
                  <div
                    key={hook.id}
                    className={`bg-gray-700 rounded-lg p-4 border-2 ${
                      log.some(l => l.toLowerCase().includes(hook.name.toLowerCase()))
                        ? 'border-green-500 animate-pulse'
                        : 'border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{hook.icon}</span>
                      <h3 className="font-bold">{hook.name}</h3>
                    </div>
                    <p className="text-sm text-gray-400 mb-2">{hook.description}</p>
                    <div className="text-xs">
                      <span className="text-blue-400">Trigger:</span> {hook.trigger}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">📊 Build Phases</h2>
              <div className="space-y-4">
                {buildPhases.map((phase, index) => (
                  <div
                    key={phase.id}
                    className={`bg-gray-700 rounded-lg p-4 border-2 ${
                      index === currentPhase && isRunning
                        ? 'border-blue-500 shadow-lg shadow-blue-500/20'
                        : index < currentPhase
                        ? 'border-green-500'
                        : 'border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold">{phase.name}</h3>
                      <div className="flex gap-1">
                        {phase.hooks.map(hookId => {
                          const hook = hooks.find(h => h.id === hookId);
                          return hook ? (
                            <span key={hookId} className="text-sm" title={hook.name}>
                              {hook.icon}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                    <div className="space-y-1">
                      {phase.prompts.map((prompt, pIndex) => (
                        <div
                          key={pIndex}
                          className={`text-sm p-2 rounded ${
                            index === currentPhase && pIndex === currentStep && isRunning
                              ? 'bg-blue-600 text-white'
                              : index < currentPhase || (index === currentPhase && pIndex < currentStep)
                              ? 'bg-green-600/20 text-green-400'
                              : 'bg-gray-600/50 text-gray-400'
                          }`}
                        >
                          {pIndex + 1}. {prompt}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">🎮 Control Panel</h2>
              <div className="space-y-4">
                <button
                  onClick={startAutonomousBuild}
                  disabled={isRunning}
                  className={`w-full py-3 rounded-lg font-bold transition-all ${
                    isRunning
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700'
                  }`}
                >
                  {isRunning ? '🔄 Building...' : '🚀 Start Autonomous Build'}
                </button>

                <button
                  onClick={() => setIsRunning(false)}
                  disabled={!isRunning}
                  className="w-full py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:text-gray-500 rounded-lg transition-colors"
                >
                  ⏸ Pause
                </button>
              </div>

              <div className="mt-6 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Phase:</span>
                  <span className="font-bold">{currentPhase + 1}/4</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Tests Written:</span>
                  <span className="font-bold text-yellow-400">{agentState.testsWritten}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Tests Passing:</span>
                  <span className="font-bold text-green-400">{agentState.testsPassing}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Files Created:</span>
                  <span className="font-bold text-blue-400">{agentState.filesCreated.length}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">🔄 Prompt Patterns</h2>
              <div className="space-y-4">
                {patterns.map(pattern => (
                  <div key={pattern.name} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{pattern.icon}</span>
                      <h3 className="font-bold">{pattern.name}</h3>
                    </div>
                    <div className="space-y-1">
                      {pattern.steps.map((step, index) => (
                        <div key={index} className="text-xs text-gray-400">
                          {index + 1}. {step}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">📜 Build Log</h2>
          <div className="bg-black rounded-lg p-4 h-64 overflow-y-auto font-mono text-sm">
            {log.length === 0 ? (
              <div className="text-gray-500">Waiting to start build...</div>
            ) : (
              log.map((entry, index) => (
                <div
                  key={index}
                  className={`mb-1 ${
                    entry.includes('✅') ? 'text-green-400' :
                    entry.includes('❌') ? 'text-red-400' :
                    entry.includes('🎯') ? 'text-blue-400' :
                    entry.includes('🎉') ? 'text-yellow-400' :
                    'text-gray-300'
                  }`}
                >
                  {entry}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-8 p-6 bg-blue-900/20 border border-blue-500/30 rounded-lg">
          <h3 className="font-bold text-blue-400 mb-2">🔮 The Vision</h3>
          <p className="text-sm text-gray-300 mb-4">
            This demonstrates how Claude could autonomously build complete features by chaining prompts through hooks.
            Each phase triggers the next, errors self-correct, and progress inexorably moves toward deployment.
          </p>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-400">1</div>
              <div className="text-xs text-gray-400">Initial Prompt</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-400">∞</div>
              <div className="text-xs text-gray-400">Autonomous Steps</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">100%</div>
              <div className="text-xs text-gray-400">Complete Feature</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}